import { ILanguageModel, IScreenDesignInfo } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import _ from "lodash";
import * as React from "react";
import { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import {
  ICollectAllTranslatablePhrasesResponse,
  ILanguagePhrase,
  ISaveTranslationsRequest,
  ScreenDesignTranslationService,
  TranslationFilterType
} from "../../../../services/screen-design-translation-service";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import CloseConfirmationModal from "../../../common/close-confirmation-modal";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaPopover from "../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTable, { KuikaTableColumn } from "../../../ui-kit/kuika-table/kuika-table";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import { failureMessageOr, toastService } from "../../../ui-kit/kuika-toast/toast-service";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaH5 from "../../../ui-kit/typography/H5/H5";
import { ImportExcelModal } from "./import-excel-modal";
import "./language-translate-modal.scss";

const ALL_SCREENS: string = "all-screens";

type TranslationStatusFilter = "all" | "not-translated" | "translated";
type TranslationViewMode = "stack" | "separate";
type ExportTranslationFilter = "all" | "exclude-not-translated" | "exclude-translated";
/** The writes that hold the dialog: each blocks the body and names itself under the spinner. */
type PendingOperation = "save" | "export" | "delete";

const TRANSLATION_STATUS_FILTERS: readonly TranslationStatusFilter[] = ["all", "not-translated", "translated"];
const EXPORT_FILTERS: readonly ExportTranslationFilter[] = ["all", "exclude-not-translated", "exclude-translated"];

const PENDING_OPERATION_LABELS: Record<PendingOperation, string> = {
  save: "Saving translations…",
  export: "Exporting…",
  delete: "Deleting translations…"
};

/** Delete borrows the confirmation's commit and says what it is doing; its outcome is a toast. */
const DELETE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Deleting" };

const EXPORT_TRANSLATION_FILTERS: Record<ExportTranslationFilter, TranslationFilterType> = {
  all: TranslationFilterType.All,
  "exclude-not-translated": TranslationFilterType.ExcludeNotTranslated,
  "exclude-translated": TranslationFilterType.ExcludeTranslated
};

const EXPORT_FILTER_OPTIONS: KuikaSelectboxOption[] = [
  { label: "All texts", value: "all" },
  { label: "Translated only", value: "exclude-not-translated" },
  { label: "Not translated only", value: "exclude-translated" }
];

/** Past this many characters a source text is clamped, so its full wording moves to a tooltip. */
const SOURCE_TOOLTIP_THRESHOLD: number = 100;

interface ILanguagePhraseExtended extends ILanguagePhrase {
  screenId: string;
  screenName: string;
}

interface ITranslationCounts {
  all: number;
  translated: number;
  notTranslated: number;
}

interface ITranslationStatusTab {
  value: TranslationStatusFilter;
  label: string;
}

interface ITranslateEmptyState {
  icon: React.ReactNode;
  title: string;
  description: string;
}

export interface ILanguageModalState {
  languagePhrases: ILanguagePhraseExtended[];
  originalLanguagePhrases: ILanguagePhraseExtended[];
  searchText: string;
  /** The phrase list is loading (on open, and again after an import) — the table shows its skeleton. */
  isFetching: boolean;
  /** A write in flight. Null when the dialog is idle. */
  pendingOperation: PendingOperation | null;
  selectedScreen: string;
  updatedValues: Record<string, string>;
  translationFilter: TranslationStatusFilter;
  viewFilter: TranslationViewMode;
  /**
   * The rows whose input has focus. They stay in the list until blur even when the status
   * filter no longer matches them — one key for a phrase input, every phrase of the group
   * for a group's shared input, so neither vanishes on its first keystroke.
   */
  editingPhraseKeys: string[];
  effectiveTranslationFilter: TranslationStatusFilter;
  expandedGroups: string[];
  showImportExcelModal: boolean;
  showExportExcelPopover: boolean;
  exportScreenSelection: string;
  exportTranslationFilter: ExportTranslationFilter;
  showDeleteConfirmModal: boolean;
  deleteScreenSelection: string;
  showCloseConfirmModal: boolean;
  /** Where Update stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where the delete confirmation's commit stands. Owned by `SaveStatus`. */
  deleteStatus: KuikaButtonStatus;
}

export interface IGroupedPhrase {
  key: string;
  isGroup: boolean;
  /** The shared source text. Set on a group row and on each of its children. */
  groupKey?: string;
  phrases: ILanguagePhraseExtended[];
  /** A leaf's own phrase, or a group's first one. */
  phrase: ILanguagePhraseExtended;
  /** A group's phrase rows. KuikaTable renders them as its sub-rows. */
  children?: IGroupedPhrase[];
}

export interface ILanguageOption {
  label: string;
  value: string;
  icon: string;
}

export interface ILanguageModalProps {
  closeModal: () => void;
  showModal: boolean;
  selectedLanguage: ILanguageModel;
  sd: IScreenDesignContextState;
  languageOptions: ILanguageOption[];
}

class LanguageTranslateModal extends React.PureComponent<ILanguageModalProps, ILanguageModalState> {
  /**
   * Update's state machine. The dialog stays open after a save, so the button is where it is
   * confirmed: "Saving", then a held "Saved" (or "Try again"). An "Update & close" closes only
   * after that ✓ has been seen.
   */
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** The delete confirmation's commit. Its outcome is a toast, so it never shows a ✓. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: ILanguageModalProps) {
    super(props);
    this.state = {
      languagePhrases: [],
      originalLanguagePhrases: [],
      searchText: "",
      isFetching: false,
      pendingOperation: null,
      selectedScreen: this.props.sd.sdId ?? ALL_SCREENS,
      updatedValues: {},
      translationFilter: "all",
      viewFilter: "stack",
      editingPhraseKeys: [],
      effectiveTranslationFilter: "all",
      expandedGroups: [],
      showImportExcelModal: false,
      showExportExcelPopover: false,
      exportScreenSelection: ALL_SCREENS,
      exportTranslationFilter: "all",
      showDeleteConfirmModal: false,
      deleteScreenSelection: ALL_SCREENS,
      showCloseConfirmModal: false,
      saveStatus: "idle",
      deleteStatus: "idle"
    };

    this.debouncedUpdateLanguagePhrases = _.debounce(this.updateLanguagePhrases, 300);
  }

  debouncedUpdateLanguagePhrases: _.DebouncedFunc<(uniqueKey: string, value: string) => void>;

  // Stable, so the table keeps its column model between keystrokes. The cells read state
  // when they render, not when this array was built.
  columns: KuikaTableColumn<IGroupedPhrase>[] = [
    {
      id: "source",
      header: (): string => `Source · ${this.getBaseLanguage()}`,
      cell: (record: IGroupedPhrase): React.ReactNode => this.renderSourceCell(record),
      noWrap: true
    },
    {
      id: "value",
      header: (): string => "Translation",
      cell: (record: IGroupedPhrase): React.ReactNode => this.renderTranslationCell(record),
      noWrap: true
    }
  ];

  componentDidMount(): void {
    void this.loadTranslations();
  }

  componentWillUnmount(): void {
    this.debouncedUpdateLanguagePhrases.cancel();
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  loadTranslations = async (): Promise<void> => {
    this.setState({ isFetching: true });
    try {
      const response: ICollectAllTranslatablePhrasesResponse = await ScreenDesignTranslationService.getTranslations(
        this.props.selectedLanguage.name
      );
      const languagePhrases: ILanguagePhraseExtended[] = this.toExtendedPhrases(response);

      this.setState({
        languagePhrases,
        originalLanguagePhrases: _.cloneDeep(languagePhrases),
        updatedValues: {},
        isFetching: false
      });
    } catch (error) {
      this.setState({ isFetching: false });
      KMainFunctions.exceptionHandler(error);
    }
  };

  toExtendedPhrases = (response: ICollectAllTranslatablePhrasesResponse | undefined): ILanguagePhraseExtended[] => {
    const languagePhrases: ILanguagePhraseExtended[] = [];

    response?.translationByScreen?.forEach((screen) => {
      screen.languagePhrases.forEach((phrase: ILanguagePhrase) => {
        languagePhrases.push({
          kuikaId: phrase.kuikaId,
          propertyName: phrase.propertyName,
          tagName: phrase.tagName,
          currentFixedValue: phrase.currentFixedValue,
          value: phrase.value ?? "",
          isConfirmation: phrase.isConfirmation,
          isNotify: phrase.isNotify,
          isConfirmationAction: phrase.isConfirmationAction,
          screenId: screen.screenId,
          screenName: screen.screenName
        });
      });
    });

    return languagePhrases;
  };

  setValue = (value: string, phrase: ILanguagePhraseExtended): void => {
    const uniqueKey: string = this.getRowKey(phrase);

    this.setState((prevState: ILanguageModalState) => ({
      updatedValues: {
        ...prevState.updatedValues,
        [uniqueKey]: value
      },
      editingPhraseKeys: [uniqueKey]
    }));

    this.debouncedUpdateLanguagePhrases(uniqueKey, value);
  };

  setBatchValues = (value: string, phrases: ILanguagePhraseExtended[]): void => {
    const updatedValues: Record<string, string> = {};
    const phraseKeys: string[] = [];

    phrases.forEach((phrase: ILanguagePhraseExtended) => {
      const uniqueKey: string = this.getRowKey(phrase);
      updatedValues[uniqueKey] = value;
      phraseKeys.push(uniqueKey);
      this.debouncedUpdateLanguagePhrases(uniqueKey, value);
    });

    this.setState((prevState: ILanguageModalState) => ({
      updatedValues: {
        ...prevState.updatedValues,
        ...updatedValues
      },
      editingPhraseKeys: phraseKeys
    }));
  };

  updateLanguagePhrases = (uniqueKey: string, value: string): void => {
    this.setState((prevState: ILanguageModalState) => {
      const languagePhrases: ILanguagePhraseExtended[] = prevState.languagePhrases.map(
        (phrase: ILanguagePhraseExtended): ILanguagePhraseExtended => {
          if (this.getRowKey(phrase) === uniqueKey) {
            return { ...phrase, value };
          }
          return phrase;
        }
      );

      return { languagePhrases };
    });
  };

  convertExtendedLanguagePhraseToNormal = (languagePhrase: ILanguagePhraseExtended): ILanguagePhrase => {
    return {
      kuikaId: languagePhrase.kuikaId,
      propertyName: languagePhrase.propertyName,
      tagName: languagePhrase.tagName,
      value: languagePhrase.value,
      isConfirmation: languagePhrase.isConfirmation,
      isNotify: languagePhrase.isNotify,
      isConfirmationAction: languagePhrase.isConfirmationAction
    };
  };

  sleep = (ms: number): Promise<void> =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, ms);
    });

  /**
   * Resolves true once the translations are stored, so a caller can close on success only. The ✓
   * is held on Update before `afterConfirm` runs — that is where an "Update & close" closes.
   */
  saveTranslate = async (afterConfirm?: () => void): Promise<boolean> => {
    if (!this.saveStatus.canStart) {
      return false;
    }
    this.saveStatus.start();
    try {
      this.debouncedUpdateLanguagePhrases.flush();

      while (this.state.editingPhraseKeys.length > 0) {
        await this.sleep(50);
      }

      // Ensure all updated values are applied to languagePhrases
      const updatedLanguagePhrases: ILanguagePhraseExtended[] = this.state.languagePhrases.map(
        (phrase: ILanguagePhraseExtended): ILanguagePhraseExtended => {
          const updatedValue: string | undefined = this.state.updatedValues[this.getRowKey(phrase)];
          if (updatedValue !== undefined) {
            return { ...phrase, value: updatedValue };
          }
          return phrase;
        }
      );

      this.setState({ pendingOperation: "save", languagePhrases: updatedLanguagePhrases });

      await this.props.sd.saveScreen(true);

      const groupedByScreenId: _.Dictionary<ILanguagePhraseExtended[]> = _.groupBy(updatedLanguagePhrases, "screenId");

      const translationByScreen: ISaveTranslationsRequest["translationByScreen"] = Object.entries(groupedByScreenId)
        .map(([screenId, phrases]) => {
          return {
            screenId,
            languagePhrases: phrases
              .filter((phrase: ILanguagePhraseExtended): boolean => !_.isEmpty(phrase.value?.trim()))
              .map((phrase: ILanguagePhraseExtended): ILanguagePhrase =>
                this.convertExtendedLanguagePhraseToNormal(phrase)
              )
          };
        })
        .filter((screen) => screen.languagePhrases.length > 0);

      const request: ISaveTranslationsRequest = {
        languageId: this.props.selectedLanguage.id,
        languageName: this.props.selectedLanguage.name,
        shortName: this.props.selectedLanguage.shortName,
        isDefault: this.props.selectedLanguage.isDefault,
        translationByScreen
      };

      const isSaved: boolean = await ScreenDesignTranslationService.saveTranslations(request);

      if (!isSaved) {
        this.setState({ pendingOperation: null });
        this.saveStatus.fail();
        return false;
      }

      this.setState({
        originalLanguagePhrases: _.cloneDeep(updatedLanguagePhrases),
        updatedValues: {},
        pendingOperation: null
      });
      this.saveStatus.succeed(afterConfirm);
      return true;
    } catch (error) {
      this.setState({ pendingOperation: null });
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  };

  // ---- Closing -------------------------------------------------------------------------

  requestClose = (): void => {
    if (this.state.pendingOperation !== null) {
      return;
    }

    if (!this.hasActualChanges()) {
      this.props.closeModal();
      return;
    }

    this.setState({ showCloseConfirmModal: true });
  };

  discardAndClose = (): void => {
    this.setState({ showCloseConfirmModal: false });
    this.props.closeModal();
  };

  saveAndClose = async (): Promise<void> => {
    const isSaved: boolean = await this.saveTranslate(this.props.closeModal);

    if (isSaved) {
      // The confirmation goes now; the dialog follows once Update has held its ✓.
      this.setState({ showCloseConfirmModal: false });
      return;
    }

    // The failure has already been reported; leave the edits in the dialog to retry.
    this.setState({ showCloseConfirmModal: false });
  };

  cancelClose = (): void => {
    if (this.state.pendingOperation === "save") {
      return;
    }

    this.setState({ showCloseConfirmModal: false });
  };

  hasActualChanges = (): boolean => {
    if (Object.keys(this.state.updatedValues).length > 0) {
      return true;
    }

    return !_.isEqual(
      this.state.languagePhrases.map(({ kuikaId: id, value }) => ({ id, value })),
      this.state.originalLanguagePhrases.map(({ kuikaId: id, value }) => ({ id, value }))
    );
  };

  // ---- Toolbar ---------------------------------------------------------------------------

  handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>, value: string | number): void => {
    this.setState({ searchText: String(value) });
  };

  handleSearchFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
    event.target.select();
  };

  handleScreenChange = (value: string | number | (string | number)[] | null): void => {
    if (typeof value !== "string") {
      return;
    }

    this.setState({ selectedScreen: value });
  };

  handleTranslationFilterChange = (value: string): void => {
    const filter: TranslationStatusFilter | undefined = TRANSLATION_STATUS_FILTERS.find(
      (candidate: TranslationStatusFilter): boolean => candidate === value
    );
    if (!filter) {
      return;
    }

    this.setState({
      translationFilter: filter,
      effectiveTranslationFilter: filter
    });
  };

  handleGroupIdenticalChange = (isGrouped: boolean): void => {
    this.setState({
      viewFilter: isGrouped ? "stack" : "separate",
      expandedGroups: []
    });
  };

  handleImportFromExcel = (): void => {
    this.setState({ showImportExcelModal: true });
  };

  handleImportSuccess = async (): Promise<void> => {
    await this.loadTranslations();
  };

  closeImportExcelModal = (): void => {
    this.setState({ showImportExcelModal: false });
  };

  // ---- Export ----------------------------------------------------------------------------

  handleExportPopoverVisibleChange = (isVisible: boolean): void => {
    if (!isVisible) {
      this.setState({ showExportExcelPopover: false });
      return;
    }

    // Each opening starts from the screen the table is showing.
    this.setState({ showExportExcelPopover: true, exportScreenSelection: this.state.selectedScreen });
  };

  closeExportPopover = (): void => {
    this.setState({ showExportExcelPopover: false });
  };

  handleExportScreenChange = (value: string | number | (string | number)[] | null): void => {
    if (typeof value !== "string") {
      return;
    }

    this.setState({ exportScreenSelection: value });
  };

  handleExportTranslationFilterChange = (value: string | number | (string | number)[] | null): void => {
    const filter: ExportTranslationFilter | undefined = EXPORT_FILTERS.find(
      (candidate: ExportTranslationFilter): boolean => candidate === value
    );
    if (!filter) {
      return;
    }

    this.setState({ exportTranslationFilter: filter });
  };

  /**
   * The export is built on the server, so one toast carries it from "Exporting" to its outcome.
   * The dialog keeps its own lock meanwhile, as it does for every operation.
   */
  proceedWithExport = async (): Promise<void> => {
    const exportScreenSelection: string = this.state.exportScreenSelection;
    const screenId: string | undefined = exportScreenSelection === ALL_SCREENS ? undefined : exportScreenSelection;

    this.setState({ pendingOperation: "export", showExportExcelPopover: false });
    const work: Promise<void> = (async (): Promise<void> => {
      const blob: Blob = await ScreenDesignTranslationService.exportTranslations(
        this.props.selectedLanguage.name,
        EXPORT_TRANSLATION_FILTERS[this.state.exportTranslationFilter],
        screenId
      );

      const url: string = URL.createObjectURL(blob);
      const link: HTMLAnchorElement = document.createElement("a");
      link.href = url;

      const projectName: string = this.props.sd.project?.name?.toLowerCase().replace(/\s+/g, "_") || "";
      let filename: string = projectName ? `${projectName}_` : "";

      if (screenId !== undefined) {
        const screen: IScreenDesignInfo | undefined = this.props.sd.screenList.find(
          (sd: IScreenDesignInfo): boolean => sd.id === screenId
        );
        const screenName: string | undefined = screen?.name;

        if (screenName) {
          filename += `${screenName.toLowerCase().replace(/\s+/g, "_")}_`;
        }
      }

      filename += `${this.props.selectedLanguage.name.toLowerCase()}_translations`;
      link.download = `${filename}.xlsx`;

      document.body.appendChild(link);
      link.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    })();
    toastService.promise(work, {
      loading: "Exporting translations…",
      success: "Translations exported",
      // The backend's own message when it sent one.
      error: failureMessageOr("The translations could not be exported.")
    });

    try {
      await work;
    } catch {
      // The toast has said why.
    } finally {
      this.setState({ pendingOperation: null });
    }
  };

  // ---- Delete ----------------------------------------------------------------------------

  openDeleteConfirm = (): void => {
    this.setState({ showDeleteConfirmModal: true, deleteScreenSelection: this.state.selectedScreen });
  };

  closeDeleteConfirm = (): void => {
    if (this.state.pendingOperation === "delete") {
      return;
    }

    this.setState({ showDeleteConfirmModal: false });
  };

  handleDeleteScreenChange = (value: string | number | (string | number)[] | null): void => {
    if (typeof value !== "string") {
      return;
    }

    this.setState({ deleteScreenSelection: value });
  };

  handleDeleteAllTranslations = async (): Promise<void> => {
    const deleteScreenSelection: string = this.state.deleteScreenSelection;
    const isAllScreens: boolean = deleteScreenSelection === ALL_SCREENS;

    if (!this.deleteStatus.canStart) {
      return;
    }
    this.deleteStatus.start();
    try {
      this.setState({ pendingOperation: "delete" });

      let isDeleted: boolean;
      if (isAllScreens) {
        isDeleted = await ScreenDesignTranslationService.deleteAllTranslations(this.props.selectedLanguage.id);
      } else {
        isDeleted = await ScreenDesignTranslationService.deleteScreenTranslations(
          this.props.selectedLanguage.id,
          deleteScreenSelection
        );
      }

      if (!isDeleted) {
        this.setState({ pendingOperation: null });
        this.deleteStatus.fail();
        KMainFunctions.displayErrorNotification("Failed to delete translations");
        return;
      }

      // The translations are gone and the confirmation with them: the toast says so, not a ✓.
      this.deleteStatus.reset();

      if (isAllScreens) {
        this.setState({
          languagePhrases: this.state.languagePhrases.map((phrase: ILanguagePhraseExtended) => ({
            ...phrase,
            value: ""
          })),
          originalLanguagePhrases: this.state.originalLanguagePhrases.map((phrase: ILanguagePhraseExtended) => ({
            ...phrase,
            value: ""
          })),
          updatedValues: {},
          pendingOperation: null,
          showDeleteConfirmModal: false
        });
        KMainFunctions.displaySuccessNotification("All translations deleted successfully");
        return;
      }

      // Only the deleted screen is reset. Its phrases lose their value in both the working copy
      // and the baseline, and its pending edits are dropped; every other screen keeps its unsaved
      // edits AND stays measured against its old baseline, so those edits still count as changes.
      const clearDeletedScreen: (phrase: ILanguagePhraseExtended) => ILanguagePhraseExtended = (
        phrase: ILanguagePhraseExtended
      ): ILanguagePhraseExtended => (phrase.screenId === deleteScreenSelection ? { ...phrase, value: "" } : phrase);
      const remainingUpdatedValues: Record<string, string> = {};
      this.state.languagePhrases.forEach((phrase: ILanguagePhraseExtended): void => {
        const rowKey: string = this.getRowKey(phrase);
        if (phrase.screenId !== deleteScreenSelection && rowKey in this.state.updatedValues) {
          remainingUpdatedValues[rowKey] = this.state.updatedValues[rowKey];
        }
      });

      this.setState({
        languagePhrases: this.state.languagePhrases.map(clearDeletedScreen),
        originalLanguagePhrases: this.state.originalLanguagePhrases.map(clearDeletedScreen),
        updatedValues: remainingUpdatedValues,
        pendingOperation: null,
        showDeleteConfirmModal: false
      });

      const screenName: string =
        this.props.sd.screenList.find((screen: IScreenDesignInfo): boolean => screen.id === deleteScreenSelection)
          ?.name || "";
      KMainFunctions.displaySuccessNotification(`Translations for screen "${screenName}" deleted successfully`);
    } catch (error) {
      this.setState({ pendingOperation: null });
      this.deleteStatus.fail();
      KMainFunctions.exceptionHandler(error);
    }
  };

  // ---- Rows ------------------------------------------------------------------------------

  handleInputBlur = (): void => {
    this.setState({
      editingPhraseKeys: [],
      effectiveTranslationFilter: this.state.translationFilter
    });
  };

  toggleGroupExpansion = (groupKey: string): void => {
    const expandedGroups: Set<string> = new Set(this.state.expandedGroups);

    if (expandedGroups.has(groupKey)) {
      expandedGroups.delete(groupKey);
    } else {
      expandedGroups.add(groupKey);
    }

    this.setState({ expandedGroups: Array.from(expandedGroups) });
  };

  isGroupExpanded = (row: IGroupedPhrase): boolean => {
    return row.isGroup && row.groupKey !== undefined && this.state.expandedGroups.includes(row.groupKey);
  };

  getSubRows = (row: IGroupedPhrase): IGroupedPhrase[] | undefined => {
    return row.isGroup ? row.children : undefined;
  };

  /** The selected screen and the search — the scope the status tabs count over. */
  getScopedPhrases = (): ILanguagePhraseExtended[] => {
    const { languagePhrases, selectedScreen, searchText } = this.state;
    let scopedPhrases: ILanguagePhraseExtended[] = languagePhrases;

    if (selectedScreen !== ALL_SCREENS) {
      scopedPhrases = scopedPhrases.filter(
        (phrase: ILanguagePhraseExtended): boolean => phrase.screenId === selectedScreen
      );
    }

    if (!_.isEmpty(searchText.trim())) {
      const searchLower: string = searchText.toLowerCase();
      scopedPhrases = scopedPhrases.filter((phrase: ILanguagePhraseExtended): boolean => {
        return !!(
          phrase.propertyName?.toLowerCase().includes(searchLower) ||
          phrase.tagName?.toLowerCase().includes(searchLower) ||
          phrase.value?.toLowerCase().includes(searchLower) ||
          phrase.currentFixedValue?.toLowerCase().includes(searchLower) ||
          phrase.screenName?.toLowerCase().includes(searchLower)
        );
      });
    }

    return scopedPhrases;
  };

  getTranslationCounts = (phrases: ILanguagePhraseExtended[]): ITranslationCounts => {
    const translated: number = phrases.filter(
      (phrase: ILanguagePhraseExtended): boolean => !!this.getInputValue(phrase)
    ).length;

    return { all: phrases.length, translated, notTranslated: phrases.length - translated };
  };

  filterByStatus = (phrases: ILanguagePhraseExtended[]): ILanguagePhraseExtended[] => {
    const { effectiveTranslationFilter, editingPhraseKeys } = this.state;

    if (effectiveTranslationFilter === "all") {
      return phrases;
    }

    const wantsTranslated: boolean = effectiveTranslationFilter === "translated";

    return phrases.filter((phrase: ILanguagePhraseExtended): boolean => {
      if (editingPhraseKeys.includes(this.getRowKey(phrase))) {
        return true;
      }
      const isTranslated: boolean = !!this.getInputValue(phrase);
      return isTranslated === wantsTranslated;
    });
  };

  toLeafRow = (phrase: ILanguagePhraseExtended, groupKey?: string): IGroupedPhrase => ({
    key: this.getRowKey(phrase),
    isGroup: false,
    groupKey,
    phrase,
    phrases: [phrase]
  });

  /** Groups identical source texts into parent rows; a text used once stays a plain row. */
  buildRows = (phrases: ILanguagePhraseExtended[]): IGroupedPhrase[] => {
    if (this.state.viewFilter === "separate") {
      return phrases.map((phrase: ILanguagePhraseExtended): IGroupedPhrase => this.toLeafRow(phrase));
    }

    const groupedByValue: _.Dictionary<ILanguagePhraseExtended[]> = _.groupBy(
      phrases,
      (phrase: ILanguagePhraseExtended) => phrase.currentFixedValue
    );

    return Object.entries(groupedByValue).map(([fixedValue, groupPhrases]): IGroupedPhrase => {
      if (groupPhrases.length === 1) {
        return this.toLeafRow(groupPhrases[0]);
      }

      return {
        key: `group_${fixedValue}`,
        isGroup: true,
        groupKey: fixedValue,
        phrase: groupPhrases[0],
        phrases: groupPhrases,
        children: groupPhrases.map((phrase: ILanguagePhraseExtended): IGroupedPhrase =>
          this.toLeafRow(phrase, fixedValue)
        )
      };
    });
  };

  getRowClassName = (record: IGroupedPhrase): string | undefined => {
    if (!record.isGroup && this.getInputValue(record.phrase)) {
      return "kuika-translate__row--translated";
    }

    return undefined;
  };

  getBaseLanguage = (): string => {
    const defaultLanguage: ILanguageModel | undefined = this.props.sd.project?.languages?.find(
      (language: ILanguageModel): boolean => language.isDefault
    );

    // The Source column is the design-time text, which is in the project's default language —
    // not the language being translated into. Same rule as the Import Excel preview.
    if (this.props.selectedLanguage.id === defaultLanguage?.id || !defaultLanguage) {
      return "Default";
    }

    return defaultLanguage.shortName;
  };

  getRowKey = (record: ILanguagePhraseExtended): string => {
    return `${record.screenId}_${record.kuikaId}_${record.propertyName}`;
  };

  getInputValue = (record: ILanguagePhraseExtended): string => {
    const uniqueKey: string = this.getRowKey(record);
    if (uniqueKey in this.state.updatedValues) {
      return this.state.updatedValues[uniqueKey];
    }
    return record.value;
  };

  getScreenOptions = (): KuikaSelectboxOption[] => [
    { label: "All screens", value: ALL_SCREENS },
    ...this.props.sd.screenList.map((screen: IScreenDesignInfo): KuikaSelectboxOption => ({
      label: screen.name,
      value: screen.id
    }))
  ];

  getEmptyState = (): ITranslateEmptyState => {
    if (!_.isEmpty(this.state.searchText.trim())) {
      return {
        icon: <KuikaSvg name="search-off" width={40} height={40} />,
        title: "No matching texts",
        description: "Try another search, or look on a different screen."
      };
    }

    if (this.state.effectiveTranslationFilter === "not-translated") {
      return {
        icon: <KuikaSvg name="circle-check" width={40} height={40} />,
        title: "Everything here is translated",
        description: "Open All to review the translations."
      };
    }

    if (this.state.effectiveTranslationFilter === "translated") {
      return {
        icon: <KuikaSvg name="language" type="sidebar-icons" width={40} height={40} />,
        title: "Nothing translated yet",
        description: "Texts appear here once they have a translation."
      };
    }

    return {
      icon: <KuikaSvg name="language" type="sidebar-icons" width={40} height={40} />,
      title: "No translatable texts",
      description: "Text in your components shows up here to translate."
    };
  };

  // ---- Rendering -------------------------------------------------------------------------

  renderSourceCell = (record: IGroupedPhrase): React.ReactNode => {
    const { phrase } = record;
    const sourceText: string = phrase.currentFixedValue ?? "";
    const tagName: string = phrase.tagName ?? "";

    let sourceLine: React.ReactNode;
    if (_.isEmpty(sourceText.trim())) {
      sourceLine = <span className="kuika-translate__source-text kuika-translate__source-text--empty">Empty</span>;
    } else {
      sourceLine = (
        <KuikaTooltip content={sourceText.length > SOURCE_TOOLTIP_THRESHOLD ? sourceText : null}>
          <span className="kuika-translate__source-text">{sourceText}</span>
        </KuikaTooltip>
      );
    }

    let toggle: React.ReactNode = null;
    let handleCellClick: (() => void) | undefined;
    let meta: React.ReactNode;

    if (record.isGroup && record.groupKey !== undefined) {
      const groupKey: string = record.groupKey;
      const isExpanded: boolean = this.isGroupExpanded(record);

      handleCellClick = (): void => this.toggleGroupExpansion(groupKey);
      toggle = (
        <KuikaButton
          type="tertiary"
          color="neutral"
          size="sm"
          isIcon
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "Collapse group" : "Expand group"}
          onClick={(event?: React.MouseEvent<HTMLButtonElement>): void => {
            event?.stopPropagation();
            this.toggleGroupExpansion(groupKey);
          }}
        >
          <KuikaSvg type="arrows" name={isExpanded ? "chevron-down" : "chevron-right"} width={16} height={16} />
        </KuikaButton>
      );
      meta = <KuikaBadge color="blue" variant="subtle" size="small" content={`${record.phrases.length} uses`} />;
    } else {
      meta = (
        <>
          {tagName !== "" && (
            // The cell ellipsizes the name at whatever width the column has; the tooltip carries it whole.
            <KuikaTooltip content={tagName}>
              <span className="kuika-translate__tag">{tagName}</span>
            </KuikaTooltip>
          )}
          {this.state.selectedScreen === ALL_SCREENS && !!phrase.screenName && (
            <KuikaBadge color="grey" variant="subtle" size="small" content={phrase.screenName} />
          )}
        </>
      );
    }

    return (
      <div
        className={classNames("kuika-translate__source", { "kuika-translate__source--group": record.isGroup })}
        onClick={handleCellClick}
      >
        {toggle}
        <div className="kuika-translate__source-body">
          {sourceLine}
          <div className="kuika-translate__meta">{meta}</div>
        </div>
      </div>
    );
  };

  renderTranslationCell = (record: IGroupedPhrase): React.ReactNode => {
    if (!record.isGroup) {
      return (
        <KuikaInput
          data-testid="languageTranslationInput"
          size="sm"
          value={this.getInputValue(record.phrase)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
            this.setValue(String(value), record.phrase)
          }
          onBlur={this.handleInputBlur}
          placeholder="Type here..."
        />
      );
    }

    const values: string[] = record.phrases.map((phrase: ILanguagePhraseExtended): string =>
      this.getInputValue(phrase)
    );
    const firstValue: string = values[0];
    const isAllSameValue: boolean = values.every((value: string): boolean => value === firstValue);
    const translatedCount: number = values.filter((value: string): boolean => !_.isEmpty(value)).length;

    if (isAllSameValue || translatedCount === 0) {
      return (
        <KuikaInput
          data-testid="languageTranslationInput"
          size="sm"
          value={firstValue}
          onChange={(event: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
            this.setBatchValues(String(value), record.phrases)
          }
          onBlur={this.handleInputBlur}
          placeholder="Type here to update all..."
        />
      );
    }

    if (translatedCount < values.length) {
      return (
        <span className="kuika-translate__group-value">
          Partially translated ({translatedCount}/{values.length})
        </span>
      );
    }

    return <span className="kuika-translate__group-value">Multiple translations</span>;
  };

  renderExportPopoverContent = (screenOptions: KuikaSelectboxOption[]): React.ReactNode => {
    return (
      <div className="kuika-translate__export">
        <span className="kuika-translate__export-title">Export to Excel</span>

        <div className="kuika-translate__field">
          <label className="kuika-translate__label" htmlFor="kuika-translate-export-screen">
            Screen
          </label>
          <KuikaSelectbox
            id="kuika-translate-export-screen"
            searchable
            value={this.state.exportScreenSelection}
            onChange={this.handleExportScreenChange}
            options={screenOptions}
          />
        </div>

        <div className="kuika-translate__field">
          <label className="kuika-translate__label" htmlFor="kuika-translate-export-include">
            Include
          </label>
          <KuikaSelectbox
            id="kuika-translate-export-include"
            value={this.state.exportTranslationFilter}
            onChange={this.handleExportTranslationFilterChange}
            options={EXPORT_FILTER_OPTIONS}
          />
        </div>

        <div className="kuika-translate__export-actions">
          <KuikaButton type="tertiary" color="neutral" uppercase onClick={this.closeExportPopover}>
            Cancel
          </KuikaButton>
          <KuikaButton type="primary" uppercase onClick={this.proceedWithExport}>
            Export
          </KuikaButton>
        </div>
      </div>
    );
  };

  render(): React.ReactNode {
    const { selectedLanguage, languageOptions, showModal } = this.props;
    const {
      pendingOperation,
      isFetching,
      searchText,
      selectedScreen,
      translationFilter,
      viewFilter,
      showExportExcelPopover
    } = this.state;

    const lang: ILanguageOption | undefined = languageOptions.find(
      (option: ILanguageOption): boolean => option.value === selectedLanguage.name
    );
    const scopedPhrases: ILanguagePhraseExtended[] = this.getScopedPhrases();
    const counts: ITranslationCounts = this.getTranslationCounts(scopedPhrases);
    const rows: IGroupedPhrase[] = this.buildRows(this.filterByStatus(scopedPhrases));
    const screenOptions: KuikaSelectboxOption[] = this.getScreenOptions();
    const emptyState: ITranslateEmptyState = this.getEmptyState();
    const isSaving: boolean = pendingOperation !== null;
    const savingLabel: string | undefined = pendingOperation ? PENDING_OPERATION_LABELS[pendingOperation] : undefined;

    const statusTabs: ITranslationStatusTab[] = [
      { value: "all", label: `All · ${counts.all}` },
      { value: "not-translated", label: `Not translated · ${counts.notTranslated}` },
      { value: "translated", label: `Translated · ${counts.translated}` }
    ];

    return (
      <>
        <KModal
          header="Translate"
          width={980}
          showFullScreenButton
          className="kuika-translate"
          showModal={showModal}
          closeModal={this.requestClose}
          // requestClose refuses while a save is in flight; the X shows it rather than looking dead.
          closeDisabled={isSaving}
          isLoading={isSaving}
          loadingLabel={savingLabel}
          footer={
            <KModalFooter
              onDelete={this.openDeleteConfirm}
              deleteLabel="Delete translations"
              deleteOnboardingId="language-translate-modal-delete-all-translations"
              onCancel={this.requestClose}
              submitLabel="Update"
              onSubmit={(): void => void this.saveTranslate()}
              status={this.state.saveStatus}
            />
          }
        >
          <div className="kuika-translate__body">
            <div className="kuika-translate__toolbar">
              <div className="kuika-translate__toolbar-row">
                <div className="kuika-translate__language">
                  {lang && (
                    <KuikaSvg
                      className="kuika-translate__flag"
                      type="flags"
                      name={lang.icon.toLowerCase()}
                      useOriginalColors
                      width={16}
                      height={16}
                    />
                  )}
                  <span className="kuika-translate__language-name">{lang?.label ?? selectedLanguage.name}</span>
                  <div className="kuika-translate__screen-select">
                    <KuikaSelectbox
                      searchable
                      placeholder="All screens"
                      value={selectedScreen}
                      onChange={this.handleScreenChange}
                      options={screenOptions}
                    />
                  </div>
                </div>

                <div className="kuika-translate__actions">
                  <div className="kuika-translate__search">
                    <KuikaInput
                      placeholder="Search texts"
                      leftIcon={<KuikaSvg name="search" width={14} height={14} color="var(--dark-5)" />}
                      value={searchText}
                      onChange={this.handleSearchInputChange}
                      onFocus={this.handleSearchFocus}
                    />
                  </div>
                  <KuikaTooltip content="Import from Excel">
                    <KuikaButton
                      type="secondary"
                      color="neutral"
                      size="md"
                      isIcon
                      aria-label="Import from Excel"
                      data-onboarding-id="language-translate-modal-import-excel"
                      onClick={this.handleImportFromExcel}
                    >
                      <KuikaSvg name="file-import" width={16} height={16} />
                    </KuikaButton>
                  </KuikaTooltip>
                  {/* The tooltip needs a DOM trigger of its own: the popover's trigger clones its
                      child with the props that open it, and KuikaTooltip does not pass them on. */}
                  <KuikaTooltip content="Export to Excel">
                    <span className="kuika-translate__tooltip-anchor">
                      <KuikaPopover
                        content={this.renderExportPopoverContent(screenOptions)}
                        hideCloseIcon
                        position="bottom"
                        align="end"
                        visible={showExportExcelPopover}
                        onVisibleChange={this.handleExportPopoverVisibleChange}
                      >
                        <KuikaButton
                          type="secondary"
                          color="neutral"
                          size="md"
                          isIcon
                          active={showExportExcelPopover}
                          aria-label="Export to Excel"
                          data-onboarding-id="language-translate-modal-export-excel"
                          // The anchor above carries the tooltip; this tells the onboarding
                          // layer not to attach a second one to the button itself.
                          data-has-kuika-tooltip="true"
                        >
                          <KuikaSvg name="file-export" width={16} height={16} />
                        </KuikaButton>
                      </KuikaPopover>
                    </span>
                  </KuikaTooltip>
                </div>
              </div>

              <div className="kuika-translate__toolbar-row">
                <div className="kuika-translate__status">
                  <KuikaTabs
                    size="sm"
                    options={statusTabs}
                    value={translationFilter}
                    onChange={this.handleTranslationFilterChange}
                  />
                </div>
                <KuikaSwitch
                  label="Group identical texts"
                  checked={viewFilter === "stack"}
                  onChange={this.handleGroupIdenticalChange}
                />
              </div>
            </div>

            <div className="kuika-translate__table">
              <KuikaTable<IGroupedPhrase>
                data={rows}
                columns={this.columns}
                rowKey={(record: IGroupedPhrase): string => record.key}
                tree={{
                  getSubRows: this.getSubRows,
                  isRowExpanded: this.isGroupExpanded,
                  treeColumnId: "source"
                }}
                rowClassName={this.getRowClassName}
                enablePagination={false}
                fitContentHeight
                loading={isFetching}
                loadingVariant="skeleton"
                emptyIcon={emptyState.icon}
                emptyTitle={emptyState.title}
                emptyDescription={emptyState.description}
              />
            </div>
          </div>
        </KModal>

        <ImportExcelModal
          showModal={this.state.showImportExcelModal}
          closeModal={this.closeImportExcelModal}
          selectedLanguage={this.props.selectedLanguage}
          languages={this.props.sd.project?.languages ?? []}
          screenList={this.props.sd.screenList}
          onImportSuccess={this.handleImportSuccess}
        />

        <KModal
          header="Delete translations"
          width={480}
          showModal={this.state.showDeleteConfirmModal}
          closeModal={this.closeDeleteConfirm}
          footer={
            <KModalFooter
              onCancel={this.closeDeleteConfirm}
              submitLabel="Delete"
              submitTone="danger"
              onSubmit={this.handleDeleteAllTranslations}
              status={this.state.deleteStatus}
              statusLabels={DELETE_STATUS_LABELS}
            />
          }
        >
          <div className="kuika-translate__delete">
            <div className="kuika-translate__delete-header">
              <KuikaModalIconWrapper iconName="trash" intent="destructive" />
              <div className="kuika-translate__delete-title">
                <KuikaH5>Delete translations</KuikaH5>
                <p className="kuika-translate__delete-text">
                  Choose which screen&apos;s {selectedLanguage.shortName} translations to delete. This can&apos;t be
                  undone.
                </p>
              </div>
            </div>
            <div className="kuika-translate__field">
              <label className="kuika-translate__label" htmlFor="kuika-translate-delete-screen">
                Screen
              </label>
              <KuikaSelectbox
                id="kuika-translate-delete-screen"
                searchable
                value={this.state.deleteScreenSelection}
                onChange={this.handleDeleteScreenChange}
                options={screenOptions}
              />
            </div>
          </div>
        </KModal>

        <CloseConfirmationModal
          triggerModal={this.state.showCloseConfirmModal}
          closeType="Translations"
          itemName={`${selectedLanguage.name} translations`}
          isLoading={pendingOperation === "save"}
          closeWithoutSaveHandler={this.discardAndClose}
          updateAndCloseHandler={this.saveAndClose}
          cancel={this.cancelClose}
        />
      </>
    );
  }
}

const languageTranslateModal = LanguageTranslateModal;
export { languageTranslateModal as LanguageTranslateModal };
